<template>
    <div class="global_sidebar course-sidebar" :class="{ 'course-sidebar--dark': Boolean(dark) }">
        <header class="course-sidebar__header">
            <div class="course-sidebar__toolbar">
                <button
                    type="button"
                    class="course-sidebar__back"
                    :aria-label="$t('student.sidebar.backToDashboard')"
                    @click="backPage()"
                >
                    <v-icon size="18" aria-hidden="true">mdi-arrow-left</v-icon>
                    <span>{{ $t('common.back') }}</span>
                </button>
                <button
                    v-show="largura < 960"
                    type="button"
                    class="course-sidebar__close"
                    :aria-label="$t('student.header.closeLessons')"
                    @click="storeShowSidebar({ showSidebar: false })"
                >
                    <v-icon size="20">mdi-close</v-icon>
                </button>
            </div>

            <div class="course-sidebar__course">
                <span class="course-sidebar__eyebrow">{{ $t('student.header.course') }}</span>
                <h2
                    class="course-sidebar__title"
                    :title="courseTitleFull || undefined"
                >
                    {{ courseTitleFull }}
                </h2>
                <div
                    v-if="totalLessonsCount > 0"
                    class="course-sidebar__progress"
                >
                    <div class="course-sidebar__summary">
                        <span class="course-sidebar__total">{{ courseLessonsSummary }}</span>
                        <strong class="course-sidebar__progress-label">{{ progressPercentUi }}%</strong>
                    </div>
                    <div
                        class="course-sidebar__progress-track"
                        role="progressbar"
                        :aria-valuenow="progressPercentUi"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        :aria-label="$t('student.header.courseProgressLabel')"
                    >
                        <div
                            class="course-sidebar__progress-fill"
                            :class="{ 'is-complete': progressPercentUi >= 100 }"
                            :style="{ width: progressPercentUi + '%' }"
                        />
                    </div>
                </div>
            </div>

            <div class="course-sidebar__search">
                <v-text-field
                    v-model="searchQuery"
                    type="search"
                    name="weeduc_course_lesson_search"
                    :label="$t('student.sidebar.searchInCourse')"
                    :placeholder="$t('student.sidebar.searchInCourse')"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    autocomplete="one-time-code"
                    autocapitalize="off"
                    autocorrect="off"
                    spellcheck="false"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    readonly
                    @focus="onSearchFocus"
                />
            </div>
        </header>

        <div class="course-sidebar__scroll">
            <p
                v-if="!filteredModules.length"
                class="course-sidebar__empty"
            >
                {{
                    searchQuery.trim()
                        ? $t('student.sidebar.emptySearch')
                        : $t('student.sidebar.emptyContent')
                }}
            </p>

            <v-expansion-panels
                v-else
                class="course-sidebar-panels"
                variant="accordion"
                v-model="currentModuleOpenLocal"
            >
                <v-expansion-panel
                    v-for="entry in filteredModules"
                    :key="`${entry.originalIndex}-${moduleCompletedCount(entry.item)}`"
                    :value="entry.originalIndex"
                    class="course-sidebar-panel"
                >
                    <v-expansion-panel-title
                        class="course-sidebar-panel__title"
                        expand-icon="mdi-chevron-down"
                        :aria-expanded="currentModuleOpenLocal === entry.originalIndex"
                    >
                        <div class="course-sidebar-panel__head">
                            <div class="course-sidebar-panel__meta">
                                <span class="course-sidebar-panel__name">{{ entry.item.title }}</span>
                                <span class="course-sidebar-panel__count">
                                    {{ moduleCompletedCount(entry.item) }} {{ $t('common.of') }}
                                    {{ entry.item.lessons?.length ?? 0 }} {{ $t('student.courseCard.lessonMany') }}
                                </span>
                            </div>
                            <div
                                class="course-sidebar-panel__mini-track"
                                aria-hidden="true"
                            >
                                <div
                                    class="course-sidebar-panel__mini-fill"
                                    :style="{ width: moduleProgressPercent(entry.item) + '%' }"
                                />
                            </div>
                        </div>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text class="course-sidebar-lesson-wrap">
                        <ul class="course-sidebar-lesson-list">
                            <li
                                v-for="(lesson, i) in entry.item.lessons"
                                :key="`${lesson.id || i}-${Number(lesson.progress) === 1 ? 1 : 0}`"
                            >
                                <button
                                    type="button"
                                    class="course-sidebar-lesson"
                                    :class="lessonClass(lesson)"
                                    :aria-current="isCurrentLesson(lesson) ? 'step' : undefined"
                                    :disabled="isLessonBlocked(lesson)"
                                    :aria-label="lessonAriaLabel(lesson)"
                                    @click="
                                        setCurrentLesson(
                                            lesson.id,
                                            i,
                                            lesson.type,
                                            entry.originalIndex,
                                            lesson.title,
                                            lesson
                                        )
                                    "
                                >
                                    <span class="course-sidebar-lesson__icon" aria-hidden="true">
                                        <v-icon
                                            v-if="isLessonConcluida(lesson)"
                                            size="20"
                                            color="#039855"
                                        >
                                            mdi-check-circle
                                        </v-icon>
                                        <v-icon
                                            v-else-if="isLessonBlocked(lesson)"
                                            size="20"
                                            color="#98A2B3"
                                        >
                                            mdi-lock-outline
                                        </v-icon>
                                        <v-icon
                                            v-else
                                            size="20"
                                            :color="isCurrentLesson(lesson) ? 'primary' : '#98A2B3'"
                                        >
                                            {{ lessonTypeIcon(lesson.type) }}
                                        </v-icon>
                                    </span>
                                    <span class="course-sidebar-lesson__text">
                                        <span class="course-sidebar-lesson__name">{{ lesson.title }}</span>
                                        <span
                                            v-if="isCurrentLesson(lesson)"
                                            class="course-sidebar-lesson__badge"
                                        >
                                            {{ $t('student.sidebar.currentLesson') }}
                                        </span>
                                        <span
                                            v-else-if="lessonMetaLine(lesson)"
                                            class="course-sidebar-lesson__type"
                                        >
                                            {{ lessonMetaLine(lesson) }}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
export default {
    data() {
        return {
            activePanel: 0,
            currentIndex: 0,
            nameCourse: '',
            currentModuleOpenLocal: 0,
            largura: typeof window !== 'undefined' ? window.innerWidth : 1200,
            progress: 0,
            searchQuery: '',
        };
    },
    computed: {
        ...mapState([
            'token',
            'currentLesson',
            'currentModuleOpen',
            'idLessonPortal',
            'nameCurrentLesson',
            'currentCoursePortal',
            'porcetageProgressCurrentCourse',
            'typeLessonPortal',
            'showSidebar',
            'dark',
        ]),
        courseTitleFull() {
            return (
                this.currentCoursePortal?.course?.comercialName
                || this.currentCoursePortal?.course?.name
                || this.nameCourse
                || ''
            );
        },
        progressPercentUi() {
            const total = this.totalLessonsCount;
            const done = this.completedLessonsCount;
            if (total > 0 && done != null) {
                return Math.round((done / total) * 100);
            }
            const raw = this.porcetageProgressCurrentCourse;
            const n = typeof raw === 'number' ? raw : parseFloat(raw);
            if (Number.isNaN(n)) {
                return 0;
            }
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        totalLessonsCount() {
            if (!Array.isArray(this.data)) return 0;
            return this.data.reduce((acc, mod) => acc + ((mod.lessons && mod.lessons.length) || 0), 0);
        },
        /** Soma confiável a partir de lesson.progress / lessonQtyRead por módulo. */
        completedLessonsCount() {
            if (!Array.isArray(this.data) || !this.data.length) return null;
            return this.data.reduce((acc, mod) => acc + this.moduleCompletedCount(mod), 0);
        },
        courseLessonsSummary() {
            void this.$i18n.locale;
            const total = this.totalLessonsCount;
            if (total <= 0) return '';
            const done = this.completedLessonsCount;
            if (done == null) {
                return total === 1
                    ? this.$t('student.sidebar.oneLesson')
                    : this.$t('student.sidebar.lessonsCount', { n: total });
            }
            if (total === 1) {
                return this.$t('student.sidebar.oneLessonCompleted', { done });
            }
            return this.$t('student.sidebar.lessonsOfTotalCompleted', { done, total });
        },
        filteredModules() {
            if (!Array.isArray(this.data)) return [];
            const q = String(this.searchQuery || '').trim().toLowerCase();
            return this.data
                .map((item, originalIndex) => {
                    if (!q) {
                        return { item, originalIndex };
                    }
                    const titleMatch = String(item.title || '').toLowerCase().includes(q);
                    const lessons = (item.lessons || []).filter((lesson) =>
                        String(lesson.title || '').toLowerCase().includes(q),
                    );
                    if (!titleMatch && !lessons.length) {
                        return null;
                    }
                    return {
                        item: titleMatch && !lessons.length
                            ? item
                            : { ...item, lessons },
                        originalIndex,
                    };
                })
                .filter(Boolean);
        },
    },
    watch: {
        activeModule: {
            handler(val) {
                if (val != null) {
                    this.activePanel = val;
                }
            },
            immediate: true,
        },
        currentModuleOpen: {
            handler(val) {
                if (val != null) {
                    this.currentModuleOpenLocal = val;
                }
            },
            immediate: true,
        },
        idLessonPortal: {
            handler() {
                const idx = this.findModuleIndexForCurrentLesson();
                if (idx != null) {
                    this.currentModuleOpenLocal = idx;
                }
            },
        },
        data: {
            handler(newModules) {
                if (newModules && newModules.length > 0) {
                    const idx = this.findModuleIndexForCurrentLesson();
                    if (idx != null) {
                        this.currentModuleOpenLocal = idx;
                    }
                }
            },
            immediate: true,
            deep: true,
        },
    },
    props: {
        data: Array,
        currentLesson: [String, Object, Number],
        activeModule: Number,
    },
    methods: {
        ...mapActions([
            'storeTypeLessonPortal',
            'storeIdLessonPortal',
            'storeCurrentModuleOpen',
            'storeCurrentLesson',
            'storeNameCurrentLesson',
            'storeShowSidebar',
            'storePorcetageProgressCurrentCourse',
        ]),

        onResize() {
            this.largura = window.innerWidth;
        },

        onSearchFocus(event) {
            const el = event?.target;
            if (el && el.hasAttribute('readonly')) {
                el.removeAttribute('readonly');
            }
        },

        findModuleIndexForCurrentLesson() {
            if (!Array.isArray(this.data) || this.idLessonPortal == null) return null;
            const idx = this.data.findIndex((mod) =>
                (mod.lessons || []).some((l) => String(l.id) === String(this.idLessonPortal)),
            );
            return idx >= 0 ? idx : null;
        },

        moduleCompletedCount(item) {
            const lessons = item?.lessons || [];
            const local = lessons.filter((l) => Number(l.progress) === 1).length;
            const api = Number(item?.lessonQtyRead);
            if (!Number.isNaN(api) && api > local) return api;
            return local;
        },

        moduleProgressPercent(item) {
            const total = item?.lessons?.length || 0;
            if (!total) return 0;
            return Math.round((this.moduleCompletedCount(item) / total) * 100);
        },

        lessonTypeLabel(type) {
            void this.$i18n.locale;
            const t = Number(type);
            if (t === 1) return this.$t('student.player.lessonTypeVideoLibrary');
            if (t === 2 || t === 3) return this.$t('student.player.lessonTypeVideo');
            if (t === 4) return this.$t('student.player.lessonTypePdf');
            if (t === 5) return this.$t('student.player.lessonTypeQuiz');
            return this.$t('student.player.lessonTypeDefault');
        },

        lessonTypeIcon(type) {
            const t = Number(type);
            if (t === 4) return 'mdi-file-pdf-box';
            if (t === 5) return 'mdi-help-circle-outline';
            if (t === 1) return 'mdi-video-box';
            return 'mdi-play-circle-outline';
        },

        lessonMetaLine(lesson) {
            const type = this.lessonTypeLabel(lesson.type);
            // Duração só se vier na lista de módulos (não inventar)
            const raw = lesson.videoTime;
            if (raw == null || raw === '') return type;
            const sec = Number(raw);
            if (Number.isNaN(sec) || sec <= 0) return type;
            const m = Math.max(1, Math.round(sec / 60));
            return `${type} · ${this.$t('student.sidebar.durationMinutes', { m })}`;
        },

        isCurrentLesson(lesson) {
            return String(lesson.id) === String(this.idLessonPortal);
        },

        isLessonConcluida(lesson) {
            return Number(lesson?.progress) === 1;
        },

        isLessonBlocked() {
            // Sem regra de bloqueio no front atual
            return false;
        },

        getLessonState(lesson) {
            if (this.isLessonConcluida(lesson)) return 'completed';
            if (this.isCurrentLesson(lesson)) return 'current';
            if (this.isLessonBlocked(lesson)) return 'blocked';
            return 'available';
        },

        lessonClass(lesson) {
            const state = this.getLessonState(lesson);
            return {
                'course-sidebar-lesson--active': state === 'current',
                'course-sidebar-lesson--done': state === 'completed',
                'course-sidebar-lesson--blocked': state === 'blocked',
            };
        },

        lessonAriaLabel(lesson) {
            void this.$i18n.locale;
            const state = this.getLessonState(lesson);
            const status =
                state === 'completed'
                    ? this.$t('student.sidebar.lessonStatusCompleted')
                    : state === 'current'
                        ? this.$t('student.sidebar.lessonStatusCurrent')
                        : state === 'blocked'
                            ? this.$t('student.sidebar.lessonStatusBlocked')
                            : this.$t('student.sidebar.lessonStatusAvailable');
            return `${lesson.title}, ${status}`;
        },

        setPorcetageProgressCurrentCourse(porcetageProgressCurrentCourse) {
            if (
                porcetageProgressCurrentCourse != 0
                && porcetageProgressCurrentCourse != null
            ) {
                this.storePorcetageProgressCurrentCourse({
                    porcetageProgressCurrentCourse,
                });
            }
        },

        setCurrentLesson(id, indexLesson, type, index, name, lesson) {
            if (this.isLessonBlocked(lesson)) return;

            this.storeIdLessonPortal({ idLessonPortal: id });
            this.storeCurrentLesson({ currentLesson: indexLesson });
            this.storeTypeLessonPortal({ typeLessonPortal: type });
            this.storeCurrentModuleOpen({ currentModuleOpen: index });
            this.storeNameCurrentLesson({ nameCurrentLesson: name });
            this.storeCurrentLesson({ currentLesson: lesson });
            if (window.innerWidth < 960) {
                this.storeShowSidebar({ showSidebar: false });
            }
        },

        backPage() {
            this.$router.push('/aluno/dashboard');
        },
    },
    created() {
        if (this.activeModule != null) {
            this.activePanel = this.activeModule;
        } else {
            this.activePanel = 0;
        }
        if (this.currentModuleOpen != null) {
            this.currentModuleOpenLocal = this.currentModuleOpen;
        }
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
        // Navegadores/gerenciadores de senha às vezes ignoram autocomplete e
        // preenchem e-mail neste campo; limpa após o autofill típico.
        this.$nextTick(() => {
            this.searchQuery = '';
            setTimeout(() => {
                this.searchQuery = '';
            }, 300);
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
};
</script>

<style>
.v-expansion-panel-text__wrapper {
    padding: 0 !important;
    flex: 1 1 auto;
    max-width: 100%;
}

.course-sidebar-panels .v-expansion-panel-text {
    padding: 0 !important;
}
</style>

<style scoped>
.course-sidebar {
    --cs-bg: #F5F7FA;
    --cs-surface: #FFFFFF;
    --cs-border: #E4E7EC;
    --cs-text: #1F2937;
    --cs-muted: #667085;
    --cs-accent: #1ac4cd;
    --cs-accent-soft: rgba(26, 196, 205, 0.1);
    --cs-module-bg: #FFFFFF;
    --cs-scroll: #D0D5DD;
    --cs-success: #039855;

    background-color: var(--cs-surface);
    height: 100%;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--cs-border);
}

.course-sidebar--dark {
    --cs-bg: #0f0e12;
    --cs-surface: #14131a;
    --cs-border: #2a3040;
    --cs-text: #f2f4f8;
    --cs-muted: #9aa3b2;
    --cs-module-bg: #1c1f28;
    --cs-scroll: #334155;
}

.course-sidebar__header {
    flex-shrink: 0;
    background: var(--cs-bg);
}

.course-sidebar__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px 4px;
}

.course-sidebar__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 40px;
    padding: 6px 8px;
    margin: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #667085;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
}

.course-sidebar__back:hover {
    color: #1F2937;
    background: var(--cs-accent-soft);
}

.course-sidebar__back:focus-visible {
    color: #1F2937;
    outline: 2px solid var(--cs-accent);
    outline-offset: 2px;
}

.course-sidebar--dark .course-sidebar__back {
    color: var(--cs-muted);
}

.course-sidebar--dark .course-sidebar__back:hover,
.course-sidebar--dark .course-sidebar__back:focus-visible {
    color: var(--cs-text);
}

.course-sidebar__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    border: 1px solid var(--cs-border);
    border-radius: 8px;
    background: var(--cs-surface);
    color: var(--cs-text);
    cursor: pointer;
}

.course-sidebar__course {
    padding: 12px 16px 16px;
    text-align: left;
    border-bottom: 1px solid #E4E7EC;
    background: transparent;
}

.course-sidebar--dark .course-sidebar__course {
    border-bottom-color: var(--cs-border);
}

.course-sidebar__eyebrow {
    display: block;
    margin: 0 0 0.3rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #667085;
    text-align: left;
}

.course-sidebar--dark .course-sidebar__eyebrow {
    color: var(--cs-muted);
}

.course-sidebar__title {
    margin: 0 0 0.7rem;
    width: 100%;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.4;
    color: #1F2937;
    text-align: left;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}

.course-sidebar--dark .course-sidebar__title {
    color: var(--cs-text);
}

.course-sidebar__progress {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 0;
}

.course-sidebar__summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 0.55rem;
}

.course-sidebar__total {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 400;
    color: #667085;
    text-align: left;
    min-width: 0;
}

.course-sidebar--dark .course-sidebar__total {
    color: var(--cs-muted);
}

.course-sidebar__progress-label {
    flex-shrink: 0;
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #1F2937;
    min-width: 2.75rem;
    text-align: right;
}

.course-sidebar--dark .course-sidebar__progress-label {
    color: var(--cs-text);
}

.course-sidebar__progress-track {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: #E4E7EC;
    overflow: hidden;
    position: relative;
}

.course-sidebar--dark .course-sidebar__progress-track {
    background: #2a3040;
}

.course-sidebar__progress-fill {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    border-radius: inherit;
    background: var(--cs-accent);
    transition: width 0.3s ease;
}

.course-sidebar__progress-fill.is-complete {
    background: var(--cs-success);
}

.course-sidebar__search {
    padding: 0 16px 14px;
}

.course-sidebar__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 12px 20px;
}

.course-sidebar__empty {
    margin: 1rem 0.5rem;
    font-size: 0.875rem;
    color: var(--cs-muted);
    text-align: center;
}

.course-sidebar__scroll::-webkit-scrollbar {
    width: 6px;
}
.course-sidebar__scroll::-webkit-scrollbar-thumb {
    background: var(--cs-scroll);
    border-radius: 6px;
}

:deep(.course-sidebar-panels) {
    gap: 8px;
    display: flex;
    flex-direction: column;
}

:deep(.course-sidebar-panel) {
    border-radius: 10px !important;
    overflow: hidden;
    border: 1px solid var(--cs-border) !important;
    background: var(--cs-module-bg) !important;
    box-shadow: none !important;
}

:deep(.course-sidebar-panel::before) {
    display: none;
}

:deep(.course-sidebar-panel__title) {
    padding: 12px 14px !important;
    min-height: auto !important;
    background: transparent !important;
}

.course-sidebar-panel__head {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: left;
    padding-right: 4px;
}

.course-sidebar-panel__meta {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.course-sidebar-panel__name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--cs-text);
    line-height: 1.35;
    flex: 1;
    min-width: 0;
}

.course-sidebar-panel__count {
    flex-shrink: 0;
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--cs-muted);
    white-space: nowrap;
}

.course-sidebar-panel__mini-track {
    height: 4px;
    border-radius: 999px;
    background: #E4E7EC;
    overflow: hidden;
}

.course-sidebar--dark .course-sidebar-panel__mini-track {
    background: #2a3040;
}

.course-sidebar-panel__mini-fill {
    height: 100%;
    background: var(--cs-accent);
    border-radius: inherit;
}

.course-sidebar-lesson-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.course-sidebar-lesson {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 11px 14px;
    margin: 0;
    border: none;
    border-top: 1px solid var(--cs-border);
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    min-height: 48px;
    transition: background 0.12s ease;
}

.course-sidebar-lesson:hover:not(:disabled) {
    background: rgba(26, 196, 205, 0.06);
}

.course-sidebar-lesson:focus-visible {
    outline: 2px solid var(--cs-accent);
    outline-offset: -2px;
}

.course-sidebar-lesson--active {
    background: var(--cs-accent-soft);
    box-shadow: inset 3px 0 0 0 var(--cs-accent);
}

.course-sidebar-lesson--done .course-sidebar-lesson__name {
    color: var(--cs-text);
}

.course-sidebar-lesson--blocked {
    cursor: not-allowed;
    opacity: 0.65;
}

.course-sidebar-lesson__icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
}

.course-sidebar-lesson__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.course-sidebar-lesson__name {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--cs-text);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.course-sidebar-lesson--active .course-sidebar-lesson__name {
    font-weight: 600;
}

.course-sidebar-lesson__type,
.course-sidebar-lesson__badge {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--cs-muted);
}

.course-sidebar-lesson__badge {
    color: #0f8f96;
}
</style>
